import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Grid,
  Button,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ReusableDataGrid } from '@/components/common/ReusableDataGrid';
import { FacilityDrawer } from '@/components/facility/FacilityDrawer';
import { Facility } from '@/constants/types';
import { facilityService } from '@/services';
import type { MRT_ColumnDef } from 'material-react-table';
import toast from 'react-hot-toast';

export const FacilitiesPage: React.FC = () => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);

  // Fetch facilities
  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await facilityService.list({
        sortDir: 'desc',
        limit: paginationModel.pageSize,
        offset: paginationModel.page,
        search_string: searchTerm,
        sortBy: 'name',
      });

      if (response.detail && response.detail[0]) {
        setFacilities(response.detail[0].paginatedResults);
        setTotalCount(response.detail[0].totalCount[0]?.count || 0);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
      toast.error('Failed to fetch facilities');
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchTerm]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  const handleClearSearch = () => {
    setSearchTerm('');
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleOpenDrawer = (facility?: Facility) => {
    setSelectedFacility(facility || null);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedFacility(null);
  };

  const handleSuccess = () => {
    fetchFacilities();
  };

  const handleOpenDeleteDialog = (facility: Facility) => {
    setSelectedFacility(facility);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedFacility(null);
  };

  const handleDelete = async () => {
    if (!selectedFacility) return;

    try {
      await facilityService.delete(selectedFacility._id);
      toast.success('Facility deleted successfully');
      handleCloseDeleteDialog();
      fetchFacilities();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete facility');
    }
  };

  const columns: MRT_ColumnDef<Facility>[] = [
    {
      accessorKey: 'name',
      header: 'Facility Name',
      size: 200,
    },
    {
      accessorKey: 'remarks',
      header: 'Remarks',
      size: 250,
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      size: 120,
      Cell: ({ cell }) => {
        const isActive = cell.getValue<boolean>();
        return (
          <Chip
            label={isActive ? 'ACTIVE' : 'INACTIVE'}
            color={isActive ? 'success' : 'default'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      accessorKey: 'availableSlots',
      header: 'Available Slots',
      size: 250,
      Cell: ({ cell }) => {
        const slots = cell.getValue<string[]>();
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {slots.map((slot, index) => (
              <Chip
                key={index}
                label={slot}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        );
      },
    },
    {
      accessorKey: 'nonAvailableDates',
      header: 'Non-Available Dates',
      size: 250,
      Cell: ({ cell }) => {
        const dates = cell.getValue<string[]>();
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {dates.map((date, index) => (
              <Chip
                key={index}
                label={date}
                size="small"
                variant="outlined"
                color="error"
              />
            ))}
          </Box>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenDrawer(row.original)}
            title="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleOpenDeleteDialog(row.original)}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5" component="h5" gutterBottom>
            Facilities Management
          </Typography>
          <Divider />
        </Box>
        <Button
          variant="contained"
          onClick={() => handleOpenDrawer()}
        >
          Add Facility
        </Button>
      </Box>

      {/* Search Filter */}
      <Grid container spacing={2} alignItems="center" mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Search Facilities"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPaginationModel({ ...paginationModel, page: 0 });
            }}
            placeholder="Search by name or remarks"
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
          <Button
            variant="text"
            onClick={handleClearSearch}
            startIcon={<ClearIcon />}
            title="Clear Search"
            sx={{
              '& .MuiButton-startIcon': {
                mr: 0,
                ml: 0,
                display: 'flex',
                justifyContent: 'end',
              },
            }}
            disabled={searchTerm === ''}
          />
        </Grid>
      </Grid>

      {/* Data Grid */}
      <ReusableDataGrid<Facility>
        columns={columns}
        rows={facilities}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationData={{
          totalRecords: totalCount,
        }}
        enableServerSidePagination={true}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableSorting={true}
        getRowId={(row) => row._id}
        showSerialNumber={true}
      />

      {/* Add/Edit Facility Drawer */}
      <FacilityDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        facility={selectedFacility}
        onSuccess={handleSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the facility "{selectedFacility?.name}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
